import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../../../generic-components/Heading';
import { ClientsAutoComplete } from './ClientsAutoComplete';
import { STYLES, TEXTS } from '../../consts/consts';
import { useStore } from '../../../../../store';
import { useInvoiceRecipientsQuery } from '../../../../../hooks/query/useInvoiceRecipientsQuery';
import { ActiveRecipientPresentor } from './ActiveRecipientPresentor';
import { CreateNewClient } from './CreateNewClient';
import { IInvoiceRecipient } from '../../types/types';
import { ReactComponent as SearchIcon } from '../../../../../assets/svg/search-app-center-icon.svg';

const { INVOICE_CREATION: { INVOICE_CLIENT_SECTION: componentStyles } } = STYLES;

const { INVOICE_CREATION: { INVOICE_CLIENT_SECTION: componentTexts } } = TEXTS;

export const PRESENTOR_ACTION_TYPES = {
  CLEAR: 'CLEAR',
  EDIT: 'EDIT',
};

export const InvoiceClientSection = () => {
  const [isAutoCompleteShow, setIsAutoCompleteShow] = useState<boolean>(false);
  const [isEditClientFormShowing, setIsEditClientFormShowing] = useState<boolean>(false);
  const [shouldSetInitialName, setShouldSetInitialName] = useState<boolean>(false);
  const [autoCompleteFilterKeyWord, setAutoCompleteFilterKeyWord] = useState<string>('');
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const { user: { customerId } } = useStore(state => state);
  const setInvoiceDataToUpdate = useStore(state => state.setInvoiceDataToUpdate);
  const invoiceData = useStore(state => state.invoiceData);
  const { t } = useTranslation();

  /** Get Recipients list query * */
  const {
    data: recipientList, refetch,
  } = useInvoiceRecipientsQuery({ customerId });
  /** --------------------------- */

  const handlePostSubmit = (recipientId: string) => {
    setSelectedRecipientId(null);
    setTimeout(() => {
      refetch()
        .then((res) => {
          const updatedList = res.data;
          const idx = updatedList.findIndex((recipient: IInvoiceRecipient) => recipient.externalId === recipientId);
          setSelectedRecipientId(idx !== -1 ? idx : null);
          setIsEditClientFormShowing(false);
          setShouldSetInitialName(false);
        });
    }, 500);
  };

  const handleCloseAutoComplete = () => {
    if (isAutoCompleteShow) {
      setIsAutoCompleteShow(false);
    }
  };

  const handleCreateNewClient = (isInitialName?: boolean) => {
    if (isInitialName) {
      setShouldSetInitialName(true);
    }
    setSelectedRecipientId(null);
    setIsEditClientFormShowing(true);
    setIsAutoCompleteShow(false);
  };

  const handleRecipientSelected = (clientId: string) => {
    const rIdx = recipientList.findIndex((c: IInvoiceRecipient) => c.externalId === clientId);
    if (rIdx === -1) return;
    setSelectedRecipientId(rIdx);
    setAutoCompleteFilterKeyWord('');
    handleCloseAutoComplete();
  };

  const handlePresentorAction = (type?: string) => {
    if (type && type === PRESENTOR_ACTION_TYPES.CLEAR) {
      setSelectedRecipientId(null);
      const invoiceDataToUpdate = { ...invoiceData };
      invoiceDataToUpdate.invoiceRecipient = null;
      setInvoiceDataToUpdate(invoiceDataToUpdate);
    } else if (type && type === PRESENTOR_ACTION_TYPES.EDIT) {
      setIsEditClientFormShowing(true);
      setIsAutoCompleteShow(false);
    } else {
      setIsAutoCompleteShow(true);
    }
  };
  const handleClientFormCloseClick = () => {
    setIsEditClientFormShowing(false);
    setShouldSetInitialName(false);
  };
  const activeRecipient = useMemo(() => {
    if (selectedRecipientId === null || !recipientList) {
      return null;
    }
    return recipientList[selectedRecipientId];
  }, [selectedRecipientId]);

  return (
    <div className={componentStyles.WRAPPER}>
      {isEditClientFormShowing && (
      <CreateNewClient
        onClose={handleClientFormCloseClick}
        handlePostSubmit={handlePostSubmit}
        client={activeRecipient}
        initialName={shouldSetInitialName ? autoCompleteFilterKeyWord : undefined}
      />
      )}
      <div className={componentStyles.CONTENT_WRAPPER}>
        <Heading title={t(componentTexts.BILL_TO)} className={componentStyles.SECTION_TITLE} />
        <div className={componentStyles.SELECT_CLIENT_WRAPPER}>
          {!activeRecipient && (
          <div className='flex flex-row justify-between items-center w-full border-[1px] border-bw04 rounded-md p-[20px]'>
            <input
              onFocus={() => setIsAutoCompleteShow(true)}
              onChange={(e) => setAutoCompleteFilterKeyWord(e.target.value)}
              placeholder={t(componentTexts.SELECT_CLIENT)}
              type='text'
              id='autoCompleteActivator'
              className='w-full mr-[6px] font-metricR text-bw08  outline-none'
            />
            <SearchIcon />
          </div>
          )}
          {activeRecipient && (
          <ActiveRecipientPresentor
            id='autoCompleteActivator'
            onClick={handlePresentorAction}
            client={activeRecipient}
          />
          )}
          {isAutoCompleteShow && (
          <ClientsAutoComplete
            onCreateNewClient={handleCreateNewClient}
            onClientSelection={handleRecipientSelected}
            recipientList={recipientList}
            elActivatorId='autoCompleteActivator'
            close={handleCloseAutoComplete}
            keyword={autoCompleteFilterKeyWord}
          />
          )}
        </div>
      </div>

    </div>
  );
};
